import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  FileText, 
  Upload,
  Download, 
  Eye, 
  Trash2,
  Plus,
  Search,
  Filter,
  Calendar,
  User,
  Building2,
  Users,
  FolderOpen,
  AlertCircle
} from 'lucide-react';
import { enhancedAPI } from '../services/api';

const Documents = () => {
  const [documents, setDocuments] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [selectedTenant, setSelectedTenant] = useState('all');
  const [selectedProperty, setSelectedProperty] = useState('all');
  const [activeTab, setActiveTab] = useState('all-docs');
  const [showUpload, setShowUpload] = useState(false);

  // Mock documents for demonstration
  const mockDocuments = [
    {
      id: '1',
      name: 'Master Lease Template 2024',
      type: 'template',
      category: 'lease',
      size: '2.1 MB',
      uploadedDate: '2024-01-15T00:00:00Z',
      uploadedBy: 'Admin',
      tenantId: null,
      propertyId: null,
      description: 'Standard lease agreement template',
      tags: ['template', 'legal']
    },
    {
      id: '2',
      name: 'John Smith - Signed Lease Agreement',
      type: 'lease',
      category: 'tenant-specific',
      size: '2.4 MB',
      uploadedDate: '2024-01-20T00:00:00Z',
      uploadedBy: 'Property Manager',
      tenantId: '1',
      propertyId: '1',
      description: 'Executed lease agreement for Unit 2A',
      tags: ['signed', 'active']
    },
    {
      id: '3',
      name: 'Property Insurance Certificate',
      type: 'insurance',
      category: 'property-specific',
      size: '1.8 MB',
      uploadedDate: '2024-01-10T00:00:00Z',
      uploadedBy: 'Admin',
      tenantId: null,
      propertyId: '1',
      description: 'Current insurance coverage documentation',
      tags: ['insurance', 'property', 'active']
    },
    {
      id: '4',
      name: 'Move-in Inspection - Sarah Johnson',
      type: 'inspection',
      category: 'tenant-specific',
      size: '3.2 MB',
      uploadedDate: '2024-02-01T00:00:00Z',
      uploadedBy: 'Property Manager',
      tenantId: '2',
      propertyId: '2',
      description: 'Pre-occupancy condition report with photos',
      tags: ['inspection', 'photos']
    },
    {
      id: '5',
      name: 'Building Safety Certificate',
      type: 'certificate',
      category: 'compliance',
      size: '856 KB',
      uploadedDate: '2024-01-05T00:00:00Z',
      uploadedBy: 'Admin',
      tenantId: null,
      propertyId: '1',
      description: 'Annual building safety inspection certificate',
      tags: ['safety', 'compliance', 'annual']
    },
    {
      id: '6',
      name: 'Property Tax Assessment 2024',
      type: 'tax',
      category: 'financial',
      size: '1.2 MB',
      uploadedDate: '2024-03-15T00:00:00Z',
      uploadedBy: 'Accountant',
      tenantId: null,
      propertyId: '2',
      description: 'Annual property tax assessment document',
      tags: ['tax', 'financial', '2024']
    }
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch tenants and properties for filtering
        const [tenantsData, propertiesData] = await Promise.all([
          enhancedAPI.getTenantsWithProperties(),
          enhancedAPI.getProperties()
        ]);
        
        setTenants(tenantsData || []);
        setProperties(propertiesData || []);
        
        // In real app, this would fetch documents from API
        await new Promise(resolve => setTimeout(resolve, 1000));
        setDocuments(mockDocuments);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         doc.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         doc.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesType = filterType === 'all' || doc.type === filterType;
    const matchesTenant = selectedTenant === 'all' || doc.tenantId === selectedTenant;
    const matchesProperty = selectedProperty === 'all' || doc.propertyId === selectedProperty;
    
    let matchesTab = true;
    if (activeTab === 'templates') {
      matchesTab = doc.category === 'lease' || doc.type === 'template';
    } else if (activeTab === 'tenant-docs') {
      matchesTab = doc.category === 'tenant-specific';
    } else if (activeTab === 'property-docs') {
      matchesTab = doc.category === 'property-specific' || doc.category === 'compliance';
    }
    
    return matchesSearch && matchesType && matchesTenant && matchesProperty && matchesTab;
  });

  const getTypeColor = (type) => {
    const colors = {
      lease: 'bg-blue-100 text-blue-800',
      template: 'bg-purple-100 text-purple-800',
      insurance: 'bg-green-100 text-green-800',
      inspection: 'bg-orange-100 text-orange-800',
      certificate: 'bg-yellow-100 text-yellow-800',
      tax: 'bg-red-100 text-red-800',
      financial: 'bg-gray-100 text-gray-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  const handleUpload = (file, metadata) => {
    // In real app, this would upload to the server
    console.log('Uploading file:', file.name, metadata);
    alert(`Upload functionality would be implemented here.\n\nFile: ${file.name}\nMetadata: ${JSON.stringify(metadata, null, 2)}`);
    setShowUpload(false);
  };

  const handleDownload = (doc) => {
    console.log('Downloading document:', doc.name);
    alert(`Downloading: ${doc.name}\n\nIn a real application, this would download the actual file.`);
  };

  const handleView = (doc) => {
    console.log('Viewing document:', doc.name);
    alert(`Opening: ${doc.name}\n\nIn a real application, this would open the document viewer.`);
  };

  const handleDelete = (doc) => {
    if (window.confirm(`Are you sure you want to delete "${doc.name}"?`)) {
      setDocuments(documents.filter(d => d.id !== doc.id));
    }
  };

  const getTenantName = (tenantId) => {
    if (!tenantId) return 'N/A';
    const tenant = tenants.find(t => (t.id || t._id) === tenantId);
    return tenant?.name || 'Unknown Tenant';
  };

  const getPropertyName = (propertyId) => {
    if (!propertyId) return 'N/A';
    const property = properties.find(p => (p.id || p._id) === propertyId);
    return property?.name || 'Unknown Property';
  };

  if (loading) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="p-6 text-center">
            <div className="animate-spin h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading documents...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Document Management</h1>
          <p className="text-gray-600">Manage lease agreements, certificates, and tenant documents</p>
        </div>
        <Button onClick={() => setShowUpload(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Upload Document
        </Button>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="p-6">
          <div className="space-y-4">
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search documents, descriptions, or tags..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <div className="flex gap-2">
                <select
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                  value={selectedProperty}
                  onChange={(e) => setSelectedProperty(e.target.value)}
                >
                  <option value="all">All Properties</option>
                  {properties.map(prop => (
                    <option key={prop.id || prop._id} value={prop.id || prop._id}>
                      {prop.name}
                    </option>
                  ))}
                </select>
                <select
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                  value={selectedTenant}
                  onChange={(e) => setSelectedTenant(e.target.value)}
                >
                  <option value="all">All Tenants</option>
                  {tenants.map(tenant => (
                    <option key={tenant.id || tenant._id} value={tenant.id || tenant._id}>
                      {tenant.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Document Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="all-docs">All Documents</TabsTrigger>
          <TabsTrigger value="templates">Templates</TabsTrigger>
          <TabsTrigger value="tenant-docs">Tenant Documents</TabsTrigger>
          <TabsTrigger value="property-docs">Property Documents</TabsTrigger>
        </TabsList>

        <TabsContent value="all-docs" className="space-y-4 mt-6">
          <DocumentsList 
            documents={filteredDocuments}
            onView={handleView}
            onDownload={handleDownload}
            onDelete={handleDelete}
            getTenantName={getTenantName}
            getPropertyName={getPropertyName}
            getTypeColor={getTypeColor}
          />
        </TabsContent>

        <TabsContent value="templates" className="space-y-4 mt-6">
          <DocumentsList 
            documents={filteredDocuments}
            onView={handleView}
            onDownload={handleDownload}
            onDelete={handleDelete}
            getTenantName={getTenantName}
            getPropertyName={getPropertyName}
            getTypeColor={getTypeColor}
          />
        </TabsContent>

        <TabsContent value="tenant-docs" className="space-y-4 mt-6">
          <DocumentsList 
            documents={filteredDocuments}
            onView={handleView}
            onDownload={handleDownload}
            onDelete={handleDelete}
            getTenantName={getTenantName}
            getPropertyName={getPropertyName}
            getTypeColor={getTypeColor}
          />
        </TabsContent>

        <TabsContent value="property-docs" className="space-y-4 mt-6">
          <DocumentsList 
            documents={filteredDocuments}
            onView={handleView}
            onDownload={handleDownload}
            onDelete={handleDelete}
            getTenantName={getTenantName}
            getPropertyName={getPropertyName}
            getTypeColor={getTypeColor}
          />
        </TabsContent>
      </Tabs>

      {/* Upload Modal Placeholder */}
      {showUpload && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Upload Document</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-center py-8 text-gray-500">
                <Upload className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>Upload functionality</p>
                <p className="text-sm">Coming soon</p>
              </div>
              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => setShowUpload(false)}>
                  Cancel
                </Button>
                <Button onClick={() => setShowUpload(false)}>
                  Upload
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

// Documents List Component
const DocumentsList = ({ documents, onView, onDownload, onDelete, getTenantName, getPropertyName, getTypeColor }) => {
  if (documents.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <FolderOpen className="h-16 w-16 mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No documents found</h3>
          <p className="text-gray-600">Try adjusting your search or filter criteria</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {documents.map((doc) => (
        <Card key={doc.id} className="hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-4 flex-1">
                <div className="p-3 bg-blue-50 rounded-lg">
                  <FileText className="h-6 w-6 text-blue-600" />
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-2">
                    <h3 className="text-lg font-medium text-gray-900 truncate">
                      {doc.name}
                    </h3>
                    <Badge className={getTypeColor(doc.type)}>
                      {doc.type}
                    </Badge>
                  </div>
                  
                  <p className="text-gray-600 text-sm mb-2">{doc.description}</p>
                  
                  <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 mr-1" />
                      {new Date(doc.uploadedDate).toLocaleDateString()}
                    </div>
                    <div className="flex items-center">
                      <User className="h-4 w-4 mr-1" />
                      {doc.uploadedBy}
                    </div>
                    {doc.tenantId && (
                      <div className="flex items-center">
                        <Users className="h-4 w-4 mr-1" />
                        {getTenantName(doc.tenantId)}
                      </div>
                    )}
                    {doc.propertyId && (
                      <div className="flex items-center">
                        <Building2 className="h-4 w-4 mr-1" />
                        {getPropertyName(doc.propertyId)}
                      </div>
                    )}
                    <span>{doc.size}</span>
                  </div>
                  
                  {doc.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {doc.tags.map(tag => (
                        <Badge key={tag} variant="outline" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              <div className="flex items-center space-x-2 ml-4">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onView(doc)}
                >
                  <Eye className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onDownload(doc)}
                >
                  <Download className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onDelete(doc)}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default Documents;